import React, { useState } from 'react';
import { BlogPost } from '../types';
import { BookOpen, Search, Clock, ArrowRight, Tag, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { useSite } from '../context/SiteContext';

interface BlogSectionProps {
  posts?: BlogPost[];
  isLoading?: boolean;
  onOpenPost?: (post: BlogPost) => void;
  onNavigateToPost?: (slug: string) => void;
}

export const BlogSection: React.FC<BlogSectionProps> = ({
  posts = [],
  isLoading = false,
  onOpenPost,
  onNavigateToPost,
}) => {
  const { isApollon, isFemale } = useSite();
  const [selectedCategory, setSelectedCategory] = useState<string>('Tümü');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [showAllPosts, setShowAllPosts] = useState<boolean>(false);

  const categories = isApollon
    ? ['Tümü', 'Medikal Cilt Bakımı', 'Botoks & Kırışıklık', 'Dudak & Yüz Dolgusu', 'Gençlik Aşısı & Mezoterapi', 'Kadın & Erkek Lazer']
    : isFemale
    ? ['Tümü', 'Fiyat Rehberi', 'Bölgeler & Seanslar', 'Medikal & Teknoloji', 'Kadın Bakım', 'Batık & Çilek Bacak']
    : ['Tümü', 'Fiyat Rehberi', 'Bölgeler & Seanslar', 'Medikal & Teknoloji', 'Erkek Bakım', 'Sakal & Ense'];

  const filteredPosts = posts.filter((post) => {
    const matchesCategory = selectedCategory === 'Tümü' || post.category === selectedCategory;
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (Array.isArray(post.targetKeywords) && post.targetKeywords.some((k) => k.toLowerCase().includes(searchQuery.toLowerCase())));
    const matchesTag = selectedTag ? (Array.isArray(post.targetKeywords) && post.targetKeywords.includes(selectedTag)) : true;

    return matchesCategory && matchesSearch && matchesTag;
  });

  const handlePostClick = (post: BlogPost) => {
    if (onNavigateToPost) {
      onNavigateToPost(post.slug);
    } else if (onOpenPost) {
      onOpenPost(post);
    }
  };

  const initialLimit = 4;
  const visiblePosts = (selectedCategory === 'Tümü' && !searchQuery && !selectedTag && !showAllPosts)
    ? filteredPosts.slice(0, initialLimit)
    : filteredPosts;

  const accentBadgeClass = isApollon
    ? 'bg-amber-500/10 border-amber-500/30 text-amber-950'
    : isFemale
    ? 'bg-rose-50 border-rose-300 text-rose-900'
    : 'bg-amber-50 border-amber-300 text-amber-900';

  const accentIconClass = isApollon
    ? 'text-amber-500'
    : isFemale
    ? 'text-rose-600'
    : 'text-amber-600';

  return (
    <section id="blog" className="py-12 sm:py-16 bg-white border-t border-slate-200 relative">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12 xl:px-16">
        
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto space-y-2.5 mb-8">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${accentBadgeClass} border text-xs font-bold shadow-2xs`}>
            <BookOpen className={`w-3.5 h-3.5 ${accentIconClass}`} />
            <span>
              {isApollon
                ? 'Apollon Medikal Bilgi & Estetik Merkezi'
                : isFemale
                ? 'SEO İçerik & Kadın Bilgi Merkezi'
                : 'SEO İçerik & Erkek Bilgi Merkezi'}
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
            {isApollon ? (
              <>
                Apollon Güzellik & Medikal Estetik <span className="text-amber-500">Rehberi & Blog</span>
              </>
            ) : isFemale ? (
              <>
                Bursa Kadın Lazer <span className="text-rose-600">Rehberi & Blog</span>
              </>
            ) : (
              <>
                Bursa Erkek Lazer <span className="text-amber-600">Rehberi & Blog</span>
              </>
            )}
          </h2>
          <p className="text-slate-600 text-xs sm:text-sm md:text-base leading-relaxed font-normal">
            {isApollon
              ? 'Hydrafacial medikal cilt bakımı, botoks, dudak & yüz dolgusu, gençlik aşısı ve kadın-erkek safir buz lazer epilasyon uygulamaları hakkında hekim onaylı uzman makaleler.'
              : isFemale
              ? 'Fiyat analizleri, seans sayıları, kadın cilt ve kıl yapısı, batık & çilek bacak çözümleri ve FSM Bulvarı doktor kliniği güvencesiyle ilgili merak ettiğiniz tüm uzman makaleler.'
              : 'Fiyat analizleri, seans sayıları, erkek kıl anatomisi ve FSM Bulvarı doktor kliniği güvencesiyle ilgili merak ettiğiniz tüm uzman makaleler.'}
          </p>
        </div>

        {/* Filter & Search Bar */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 sm:p-4 mb-6 space-y-3 shadow-2xs">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3">
            
            {/* Category Pills */}
            <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 no-scrollbar">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setSelectedTag('');
                    setShowAllPosts(true);
                  }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                    selectedCategory === cat && !selectedTag
                      ? isFemale
                        ? 'bg-rose-950 text-white shadow-xs'
                        : isApollon
                        ? 'bg-slate-900 text-amber-400 shadow-xs border border-amber-500/30'
                        : 'bg-slate-900 text-white shadow-xs'
                      : 'bg-white text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="relative w-full md:w-64">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Makale veya anahtar kelime ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full bg-white border border-slate-200 rounded-xl pl-8 pr-3.5 py-1.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none ${
                  isFemale ? 'focus:border-rose-500' : 'focus:border-amber-500'
                } transition-colors`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs font-bold"
                >
                  ✕
                </button>
              )}
            </div>

          </div>

          {/* Active Tag Filter Indicator */}
          {selectedTag && (
            <div className="flex items-center gap-2 pt-2 border-t border-slate-200 text-xs text-slate-600">
              <span>Filtrelenen Etiket:</span>
              <span className={`${
                isFemale ? 'bg-rose-50 text-rose-900 border-rose-200' : 'bg-amber-50 text-amber-900 border-amber-200'
              } font-bold px-2.5 py-0.5 rounded-full border flex items-center gap-1`}>
                #{selectedTag}
                <button onClick={() => setSelectedTag('')} className={`hover:${isFemale ? 'text-rose-700' : 'text-amber-700'} ml-1 font-bold`}>✕</button>
              </span>
            </div>
          )}
        </div>

        {/* Blog Post Grid */}
        {isLoading && posts.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6 w-full">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col justify-between animate-pulse">
                <div>
                  <div className="aspect-video w-full bg-slate-200" />
                  <div className="p-4 sm:p-5 space-y-3">
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                    <div className="space-y-1.5">
                      <div className="h-3 bg-slate-100 rounded w-full" />
                      <div className="h-3 bg-slate-100 rounded w-5/6" />
                    </div>
                    <div className="flex gap-1.5 pt-2">
                      <div className="h-4 bg-slate-100 rounded w-16" />
                      <div className="h-4 bg-slate-100 rounded w-20" />
                    </div>
                  </div>
                </div>
                <div className="mx-4 sm:mx-5 mb-4 pt-2.5 border-t border-slate-100 flex items-center justify-between">
                  <div className="h-3 bg-slate-100 rounded w-20" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredPosts.length > 0 ? (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6 w-full">
              {visiblePosts.map((post) => (
                <article
                  key={post.id}
                  id={`blog-card-${post.slug}`}
                  className="bg-white rounded-2xl border border-slate-200 hover:border-amber-400 overflow-hidden flex flex-col justify-between hover:shadow-md transition-all group cursor-pointer"
                  onClick={() => handlePostClick(post)}
                >
                  <div>
                    {/* Featured Image Thumbnail */}
                    <div className="relative aspect-video w-full overflow-hidden bg-slate-100">
                      <img
                        src={post.featuredImage || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&auto=format&fit=crop&q=80'}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&auto=format&fit=crop&q=80';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent opacity-40 group-hover:opacity-20 transition-opacity" />
                      
                      {/* Floating Category Badge */}
                      <div className="absolute top-3 left-3">
                        <span className="text-[10px] font-bold text-amber-900 bg-white/95 backdrop-blur-xs border border-amber-200 px-2.5 py-0.5 rounded-full shadow-xs">
                          {post.category}
                        </span>
                      </div>

                      {/* Read Time & Views on image bottom right */}
                      <div className="absolute bottom-2.5 right-3 flex items-center gap-1.5">
                        {typeof post.views === 'number' && post.views > 0 && (
                          <span className="text-[10px] text-white font-medium flex items-center gap-1 bg-slate-950/70 backdrop-blur-xs px-2 py-0.5 rounded-md">
                            <Eye className="w-3 h-3 text-amber-400" />
                            {post.views.toLocaleString('tr-TR')}
                          </span>
                        )}
                        <span className="text-[10px] text-white font-medium flex items-center gap-1 bg-slate-950/70 backdrop-blur-xs px-2 py-0.5 rounded-md">
                          <Clock className="w-3 h-3 text-amber-400" />
                          {post.readTime}
                        </span>
                      </div>
                    </div>

                    <div className="p-4 sm:p-5">
                      {/* Title */}
                      <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-amber-700 transition-colors leading-snug">
                        {post.title}
                      </h3>

                      {/* Summary */}
                      <p className="text-xs text-slate-600 line-clamp-2 mb-3 leading-relaxed font-normal">
                        {post.summary}
                      </p>

                      {/* Keywords Pills */}
                      <div className="flex flex-wrap gap-1">
                        {post.targetKeywords.slice(0, 3).map((kw, i) => (
                          <span
                            key={i}
                            className="text-[10px] bg-slate-50 text-slate-600 px-2 py-0.5 rounded-md border border-slate-200 font-medium"
                          >
                            #{kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Footer read link */}
                  <div className="mx-4 sm:mx-5 mb-4 pt-2.5 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-slate-800 group-hover:text-amber-700 transition-colors">
                    <span>Rehberi Oku</span>
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </article>
              ))}
            </div>

            {/* Expand / Collapse Toggle for default list */}
            {selectedCategory === 'Tümü' && !searchQuery && !selectedTag && filteredPosts.length > initialLimit && (
              <div className="text-center mt-6">
                <button
                  onClick={() => setShowAllPosts(!showAllPosts)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold border border-slate-300 transition-all"
                >
                  {showAllPosts ? (
                    <>
                      <span>Daha Az Makale Göster</span>
                      <ChevronUp className="w-4 h-4 text-slate-600" />
                    </>
                  ) : (
                    <>
                      <span>Tüm Makaleleri Listele ({filteredPosts.length} Makale)</span>
                      <ChevronDown className="w-4 h-4 text-amber-600" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-10 bg-slate-50 rounded-2xl border border-slate-200">
            <Tag className="w-7 h-7 text-slate-400 mx-auto mb-2" />
            <p className="text-xs sm:text-sm text-slate-600 font-semibold">Aradığınız kriterde makale bulunamadı.</p>
            <button
              onClick={() => {
                setSelectedCategory('Tümü');
                setSearchQuery('');
                setSelectedTag('');
              }}
              className="mt-2 text-xs text-amber-700 font-bold hover:underline"
            >
              Filtreleri Temizle
            </button>
          </div>
        )}

      </div>
    </section>
  );
};
